import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import adminModule from '../../../api/modules/admin.module';
import { orderTrackingModule } from '../../../api/modules/orderTracking.module';
import { useAuth } from '../../../hooks/useAuth';
import { showToast } from '../../../utils/toast';
import { OrderDetailsDrawerView } from './OrderDetailsDrawer.view';

/**
 * The order details drawer, the container: `{ isOpen, onClose, order, seller }`.
 *
 * `seller` gates the seller-only footer, and that footer carries "Confirm order",
 * the action that creates a shipping label. It defaults to `false` — fail closed.
 * A caller must opt in as the seller of the order, so a forgotten prop hides the
 * label action instead of handing it to a buyer. `OrderDetailsDrawer.test.jsx`
 * pins that. The view defaults the same way, for callers that render it directly.
 *
 * WHAT MOVED IN, AND WHY THAT DELETED A FILE
 * ------------------------------------------
 * The review form used to arrive as `pages/OrderTracking/OrderTracking1/ReviewForm/form.jsx`,
 * a container that existed for one reason: this drawer imported it, and this
 * directory was unconverted. Its own comment said so and said it collapses the
 * moment B6 lands. It has. The `submit_rating` mutation is owned here now,
 * with the same mutation key, the same `{rating, review, order_id}` payload and
 * the same `Number(value) || 5` fallback, the view renders `ReviewForm.view`
 * directly, and that directory is gone.
 *
 * `systemSettings` keeps its own query key and its five minute `staleTime`, and
 * the resubmit mutation still invalidates `['get-all-orders']`, because that is
 * the query the admin all-orders table runs.
 *
 * The body scroll lock that this file hand-rolled (including the scrollbar
 * width compensation) is gone: `Drawer` installs `lockPageScroll` through
 * `useModalLayer`, which does the same job once for every floating layer.
 */
const OrderDetailsDrawer = ({ isOpen, onClose, order, seller = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');

  const resubmitShipmentMutation = useMutation({
    mutationKey: ['resubmit-shipment'],
    mutationFn: (orderId) => orderTrackingModule.resubmitShipment(orderId),
    onSuccess: (res) => {
      if (res?.success) {
        showToast.success(res.message || 'Shipment re-submitted');
      } else {
        showToast.error(res?.message || 'Re-submit failed');
      }
      // Admin order list/detail is fed by get-all-orders; refetch so the
      // drawer's submission_status reflects the re-book attempt.
      queryClient.invalidateQueries({ queryKey: ['get-all-orders'] });
    },
    onError: () => showToast.error('Re-submit failed'),
  });

  const submitRatingMutation = useMutation({
    mutationKey: ['submit-rating'],
    mutationFn: async (payload) => orderTrackingModule.submitRating(payload),
    onSuccess: (res) => {
      if (res.success) {
        showToast.success(res.message || 'Submitted');
      } else {
        showToast.error(res.message || 'Failed to submit rating');
      }
    },
    onError: () => showToast.error('Failed to submit rating'),
  });

  // Fetch system settings to get platform fee
  const { data: systemSettings } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const result = await adminModule.getSystemSettings();
      if (result.success) {
        return result.data;
      }
      return { sell_in_person: false, platform_fee: 5 };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  if (!isOpen || !order) return null;

  const items = Array.isArray(order.items) ? order.items : [];
  const orderId = order.order_id || order.orderId || order.id || '';
  const trackingId = order.tracking_id || order.trackingId || '';

  const parsePrice = (value) => {
    if (typeof value === 'number') return value;
    return parseFloat(value?.toString().replace('R', '').trim() || '0');
  };

  /**
   * Every number here is either stated by the API (`price_breakdown`) or
   * derived from the items and the platform fee rate, both of which the app
   * really has.
   *
   * `shipping` is now among the stated ones. It used to be worked out on this
   * screen by subtracting three numbers from a fourth, which is why it was
   * dropped; `get_buyer_orders` and `get_orders` both send it, and it is an
   * exact figure rather than an estimate because checkout builds `total` as
   * the subtotal plus, for anything not in-person, the courier rate and a flat
   * R40. It stays `null` when the API did not send one, so the view can tell
   * "delivery cost nothing" apart from "nobody told us what delivery cost" --
   * printing R0.00 for the second would be inventing a fact.
   */
  const calculatePriceBreakdown = () => {
    if (order.price_breakdown) {
      const stated = order.price_breakdown.shipping;
      return {
        subtotal: parsePrice(order.price_breakdown.items_subtotal),
        shipping: stated === undefined || stated === null ? null : parsePrice(stated),
        fees: parsePrice(order.price_breakdown.fees),
        profit: parsePrice(order.price_breakdown.your_profit),
        total: parsePrice(order.price_breakdown.total_payment),
        platformFeePercentage: systemSettings?.platform_fee || 5,
      };
    }

    const itemsSubtotal = items.reduce((sum, item) => {
      const itemPrice = parsePrice(item.price);
      return sum + itemPrice * (item.quantity || 1);
    }, 0);

    let totalAmount = 0;
    if (order.payment) {
      totalAmount = parsePrice(order.payment);
    } else if (order.total) {
      totalAmount = parsePrice(order.total);
    } else {
      totalAmount = itemsSubtotal;
    }

    const platformFeePercentage = (systemSettings?.platform_fee || 5) / 100;
    const platformFees = itemsSubtotal * platformFeePercentage;

    const sellerProfit = order.profit
      ? parsePrice(order.profit)
      : itemsSubtotal - platformFees;

    return {
      subtotal: itemsSubtotal,
      // Null, not `totalAmount - itemsSubtotal`. On this branch the subtotal is
      // a client-side sum of whatever item rows arrived, so a difference
      // against the total is only as trustworthy as that list -- which is the
      // derived figure this screen was right to stop printing.
      shipping: null,
      fees: platformFees,
      profit: Math.max(sellerProfit, 0),
      total: totalAmount,
      platformFeePercentage: systemSettings?.platform_fee || 5,
    };
  };

  const handleConfirmOrder = () => {
    onClose();
    navigate('/create-label', { state: { orderId } });
  };

  const handleTrackOrder = () => {
    onClose();
    if (user?.role === 'admin') {
      navigate('/admin-order-tracking', { state: { trackingId } });
    } else {
      navigate('/order-tracking', { state: { trackingId } });
    }
  };

  const handleDownloadWaybill = () => {
    orderTrackingModule
      .getWaybill(trackingId)
      .then((response) => {
        if (response.success && response.url) {
          window.open(response.url, '_blank');
        } else {
          showToast.error(response.message || "Waybill isn't ready yet");
        }
      })
      .catch(() => showToast.error('Failed to download waybill'));
  };

  return (
    <OrderDetailsDrawerView
      open={isOpen}
      onClose={onClose}
      order={order}
      seller={seller}
      isAdmin={user?.role === 'admin'}
      priceBreakdown={calculatePriceBreakdown()}
      onConfirmOrder={handleConfirmOrder}
      onTrackOrder={handleTrackOrder}
      onDownloadWaybill={handleDownloadWaybill}
      onResubmitShipment={() => resubmitShipmentMutation.mutate(orderId)}
      resubmitBusy={resubmitShipmentMutation.isPending}
      reviewRating={rating}
      onReviewRatingChange={setRating}
      reviewText={review}
      onReviewTextChange={setReview}
      reviewBusy={submitRatingMutation.isPending}
      onReviewSubmit={({ rating: value, review: text }) =>
        submitRatingMutation.mutate({
          rating: Number(value) || 5,
          review: String(text || '').trim(),
          order_id: orderId,
        })
      }
    />
  );
};

export default OrderDetailsDrawer;
